import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SectionWrapper from '../UI/SectionWrapper';
import { Sparkles, Waves, Zap, Sun, Home } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    id: 0,
    title: "Ley Lines",
    subtitle: "The Invisible Grid", // Keep subtitle for consistency if needed, though not used in new layout
    desc: "Naturally occuring ley lines impact the frequency of a property. Positive ley lines can be enhanced and negative ley lines can be balanced.",
    color: "text-amber-600",
    bg: "bg-amber-50/50"
  },
  {
    id: 1,
    title: "Geopathic Stress",
    subtitle: "Beneath the Surface", // Keep subtitle for consistency if needed, though not used in new layout
    desc: "Both natural and man-made stresses can impact a property. These include underground water flowing or stagnant the removal of significant trees, earthquakes fracture lines, electromagnetic fields, EMF and major earthworks",
    color: "text-blue-600",
    bg: "bg-blue-50/50"
  },
  {
    id: 2,
    title: "Natural Energies",
    subtitle: "Echoes of the Past", // Keep subtitle for consistency if needed, though not used in new layout
    desc: "Energies from previous occupants and events can be resolved and cleared to bring sense of calmness.",
    color: "text-purple-600",
    bg: "bg-purple-50/50"
  },
  {
    id: 3,
    title: "Happy Land",
    subtitle: "Total Harmony", // Keep subtitle for consistency if needed, though not used in new layout
    desc: "A healed propety supports a harmonious environment for people and animals and plants. The results are more peace and ease in life and work flow.",
    color: "text-emerald-600",
    bg: "bg-emerald-50/50"
  }
];

// Individual Step Illustration Component - Elegant Line Art / Sacred Geometry Style
const StepIllustration = ({ stepId }) => {
  return (
    <div className="relative w-48 h-48 md:w-56 md:h-56 mx-auto flex items-center justify-center">
      
      {/* Background Glow (Subtle) */}
      <div className={`absolute inset-0 rounded-full blur-3xl opacity-20 transition-colors duration-700
        ${stepId === 0 ? 'bg-amber-200' : 
          stepId === 1 ? 'bg-blue-200' : 
          stepId === 2 ? 'bg-purple-200' : 'bg-emerald-200'}`} 
      />

      <svg width="200" height="200" viewBox="0 0 200 200" className="drop-shadow-sm overflow-visible">
        
        {/* COMMON ELEMENTS: The "Property" Anchor */}
        {/* A sleek, minimal house silhouette representing the physical space - Enhanced */}
        <g className="transition-all duration-700" style={{ transformOrigin: 'center' }}>
          <path 
            d="M60 110 L100 70 L140 110 V160 H60 V110Z" 
            fill="white" 
            fillOpacity="0.4"
            stroke="#57534E" // stone-600
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
           {/* Added window for more home-like detail */}
           <rect x="92" y="125" width="16" height="16" stroke="#78716C" strokeWidth="1" fill="none" opacity="0.4" />
           <line x1="100" y1="125" x2="100" y2="141" stroke="#78716C" strokeWidth="1" opacity="0.3" />
           <line x1="92" y1="133" x2="108" y2="133" stroke="#78716C" strokeWidth="1" opacity="0.3" />

          {/* Ground Line */}
          <path d="M40 160 H160" stroke="#78716C" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
          {/* Added grass tufts */}
          <path d="M45 160 L48 155 L51 160" stroke="#78716C" strokeWidth="1" fill="none" opacity="0.3" />
          <path d="M150 160 L153 155 L156 160" stroke="#78716C" strokeWidth="1" fill="none" opacity="0.3" />
        </g>

        {/* STEP 0: POSITIVE LEY LINES (Geometry/Grid) */}
        {stepId === 0 && (
          <g className="animate-[pulse_4s_ease-in-out_infinite]">
             {/* Added Background Geometry */}
             <path d="M100 80 L130 100 L120 140 H80 L70 100 Z" fill="none" stroke="#F59E0B" strokeWidth="0.5" opacity="0.2" className="animate-[spin_12s_linear_infinite]" style={{ transformOrigin: '100px 110px' }} />

             {/* Energy Lines */}
             <path d="M40 140 L160 80" stroke="#D97706" strokeWidth="1" strokeDasharray="60 120" className="animate-[dash_8s_linear_infinite]" opacity="0.6" />
             <path d="M40 80 L160 140" stroke="#D97706" strokeWidth="1" strokeDasharray="60 120" className="animate-[dash_8s_linear_infinite_reverse]" opacity="0.6" />
             
             {/* Intersection Node */}
             <circle cx="100" cy="110" r="4" fill="#FBBF24" className="animate-ping opacity-75" />
             <circle cx="100" cy="110" r="3" fill="#F59E0B" />
             
             {/* Vertical Alignment Line */}
             <line x1="100" y1="50" x2="100" y2="170" stroke="#F59E0B" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />
             
             {/* Small abstract nodes - enhanced */}
             <circle cx="40" cy="140" r="2" fill="#D97706" opacity="0.5" />
             <circle cx="160" cy="80" r="2" fill="#D97706" opacity="0.5" />
             <circle cx="40" cy="80" r="2" fill="#D97706" opacity="0.5" />
             <circle cx="160" cy="140" r="2" fill="#D97706" opacity="0.5" />
          </g>
        )}

        {/* STEP 1: UNDERGROUND WATER (Flow/Depth) */}
        {stepId === 1 && (
          <g>
             {/* Added Deep Water Stream */}
             <path 
              d="M30 190 Q70 200 110 190 T170 190" 
              fill="none" 
              stroke="#0369A1" 
              strokeWidth="1.5" 
              opacity="0.2"
              className="animate-[pulse_5s_ease-in-out_infinite]"
            />

            {/* Flowing Water Streams beneath */}
            <path 
              d="M40 170 Q70 160 100 170 T160 170" 
              fill="none" 
              stroke="#0EA5E9" 
              strokeWidth="2" 
              opacity="0.6"
              className="animate-[pulse_3s_ease-in-out_infinite]"
            />
            <path 
              d="M40 180 Q70 190 100 180 T160 180" 
              fill="none" 
              stroke="#38BDF8" 
              strokeWidth="2" 
              opacity="0.4"
              className="animate-[pulse_4s_ease-in-out_infinite]"
            />
            
            {/* Added Bubbles */}
            <circle cx="70" cy="175" r="1.5" fill="#BAE6FD" opacity="0.6" className="animate-[bounce_3s_infinite]" />
            <circle cx="130" cy="185" r="2" fill="#BAE6FD" opacity="0.5" className="animate-[bounce_4s_infinite]" />

            {/* Resonance Ripples upward */}
            <path d="M80 160 Q100 150 120 160" stroke="#0EA5E9" strokeWidth="1" opacity="0.3" />
            <path d="M85 155 Q100 148 115 155" stroke="#0EA5E9" strokeWidth="1" opacity="0.2" />
          </g>
        )}

        {/* STEP 2: CLEARING ENERGIES (Radiance/Dispersal) */}
        {stepId === 2 && (
          <g>
            {/* Radiant Center */}
            <circle cx="100" cy="115" r="30" fill="url(#cleaningGradient)" opacity="0.3" className="animate-pulse" />
            
            {/* Added Spiral Energy */}
            <path d="M100 115 m-25 0 a 25 25 0 1 1 50 0 a 25 25 0 1 1 -50 0" stroke="#C4B5FD" strokeWidth="0.5" fill="none" strokeDasharray="4 4" className="animate-[spin_4s_linear_infinite]" opacity="0.4" style={{ transformOrigin: '100px 115px' }} />

            {/* Rays of Light - More Rays */}
            <g stroke="#A855F7" strokeWidth="1" opacity="0.6">
               <line x1="100" y1="115" x2="100" y2="70" strokeDasharray="4 4" />
               <line x1="100" y1="115" x2="145" y2="115" strokeDasharray="4 4" />
               <line x1="100" y1="115" x2="55" y2="115" strokeDasharray="4 4" />
               <line x1="100" y1="115" x2="130" y2="85" strokeDasharray="4 4" />
               <line x1="100" y1="115" x2="70" y2="85" strokeDasharray="4 4" />
               <line x1="100" y1="115" x2="80" y2="150" strokeDasharray="2 4" />
               <line x1="100" y1="115" x2="120" y2="150" strokeDasharray="2 4" />
            </g>

            {/* Sparkles/Stars replacing 'dust' */}
            <path d="M130 90 L132 94 L136 96 L132 98 L130 102 L128 98 L124 96 L128 94 Z" fill="#C084FC" className="animate-[spin_4s_linear_infinite]" />
            <path d="M70 100 L71 103 L74 104 L71 105 L70 108 L69 105 L66 104 L69 103 Z" fill="#E879F9" className="animate-[spin_3s_linear_infinite]" />
            <circle cx="100" cy="90" r="1.5" fill="#D8B4FE" className="animate-ping" opacity="0.7" />

            <defs>
              <radialGradient id="cleaningGradient" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#D8B4FE" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#D8B4FE" stopOpacity="0" />
              </radialGradient>
            </defs>
          </g>
        )}

        {/* STEP 3: BALANCE (Harmony/Wholeness) */}
        {stepId === 3 && (
          <g className="animate-in fade-in duration-1000">
             {/* Protection Circle */}
             <circle cx="100" cy="115" r="65" stroke="#10B981" strokeWidth="1" strokeDasharray="1 4" fill="none" opacity="0.4" className="animate-[spin_20s_linear_infinite]" />
             <circle cx="100" cy="115" r="60" stroke="#10B981" strokeWidth="1.5" fill="none" opacity="0.2" />

             {/* Organic Growth (Leaves) */}
             <path d="M140 160 Q150 140 160 130" stroke="#059669" strokeWidth="1.5" fill="none" strokeLinecap="round" />
             <path d="M160 130 Q165 125 155 125 Z" fill="#6EE7B7" opacity="0.6" />
             <path d="M160 130 Q165 135 170 130 Z" fill="#6EE7B7" opacity="0.6" />

             <path d="M60 160 Q50 140 40 130" stroke="#059669" strokeWidth="1.5" fill="none" strokeLinecap="round" />
             <path d="M40 130 Q35 125 45 125 Z" fill="#6EE7B7" opacity="0.6" />
             <path d="M40 130 Q35 135 30 130 Z" fill="#6EE7B7" opacity="0.6" />

             {/* Added Center Sprout */}
             <path d="M100 160 Q100 150 100 140" stroke="#059669" strokeWidth="1.5" fill="none" strokeLinecap="round" />
             <path d="M100 140 Q95 135 100 130 Q105 135 100 140" fill="#6EE7B7" opacity="0.8" />

             {/* Sun/Source above - Enhanced */}
             <circle cx="100" cy="50" r="8" fill="#FCD34D" opacity="0.8" className="animate-pulse" />
             <path d="M100 60 V75" stroke="#FCD34D" strokeWidth="1" />
             {/* Sun Rays */}
             <path d="M100 38 V42" stroke="#FCD34D" strokeWidth="1" />
             <path d="M112 50 H108" stroke="#FCD34D" strokeWidth="1" />
             <path d="M88 50 H92" stroke="#FCD34D" strokeWidth="1" />
             <path d="M108 42 L106 44" stroke="#FCD34D" strokeWidth="1" />
             <path d="M92 42 L94 44" stroke="#FCD34D" strokeWidth="1" />
          </g>
        )}
        
      </svg>
    </div>
  );
};

const ProcessSteps = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".process-card", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
        }
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <SectionWrapper ref={containerRef} id="process" className="bg-white py-24">
      <div className="text-center mb-16 px-4">
        <h2 className="text-4xl md:text-5xl font-serif text-forest-green mb-6">
          How Land Healing <span className="italic">Works</span>
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
           A journey from the invisible to the harmonious.
        </p>
      </div>

      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="process-card flex flex-col items-center text-center group px-2"
            >
              <div className="w-full mb-4 flex items-center justify-center transform group-hover:-translate-y-2 transition-transform duration-500">
                <StepIllustration stepId={step.id} />
              </div>

              <div className="w-full max-w-xs transition-all duration-300">
                <h3 className={`text-2xl font-serif mb-3 ${step.color}`}>
                  {step.title}
                </h3>
                
                <p className="text-stone-600 leading-relaxed font-sans text-[15px]">
                  {step.desc}
                </p>
              </div>
            </div>
          ))}

        </div>
      </div>
    </SectionWrapper>
  );
};

export default ProcessSteps;
